import { PaymentPlan, Subscription } from 'generated/api';
import moment from 'moment-timezone';

class SubscriptionUtils {
  activeSubscriptionUntilThisMonth(sub: Subscription, date: number) {
    let { created, ended_at, cancel_at } = sub;
    let startOfPeriod = moment.unix(date).startOf('month').unix();
    if (ended_at! > 0 || cancel_at! > 0) {
      let endDate = Math.max(ended_at || 0, cancel_at || 0);
      return created! <= startOfPeriod && endDate > date;
    } else {
      return created! <= startOfPeriod;
    }
  }

  convertPlanAmount(amount: number) {
    return (amount / 100.0).toFixed(2);
  }

  isActiveSubscription(subscription: Subscription | undefined) {
    return subscription?.status === 'trialing' || subscription?.status === 'active';
  }

  isPaymentOverdue(subscription: Subscription | undefined) {
    return subscription?.status === 'past_due' || subscription?.status === 'unpaid';
  }

  isUnsubscribed(subscription: Subscription | undefined) {
    return !subscription || subscription.id === 'unsubscribed';
  }

  isScheduledToCancel(subscription: Subscription | undefined) {
    return subscription && (subscription.cancel_at_period_end === true || subscription.cancel_at);
  }

  getSubscriptionName(subscription: Subscription | undefined) {
    return subscription?.plan?.name ?? 'N/A';
  }

  getInterval(subscription: Subscription | undefined) {
    const interval = subscription?.plan?.interval;
    return interval ? `${interval}ly` : '';
  }

  isActivePayingSubscription(subscription: Subscription | undefined) {
    return !this.isUnsubscribed(subscription) && this.isActiveSubscription(subscription);
  }

  getPaymentPlanPermissions() {
    return PAYMENT_PLAN_PERMISSSIONS;
  }

  getPermissionValue(subscription: Subscription | undefined, permissionName: string) {
    if (subscription === undefined || subscription.plan === undefined) {
      return 'false';
    }

    const permission = (subscription.plan.permissions ?? []).find(
      (permission) => permission.name === permissionName,
    ) ?? { value: 'false' };

    return permission.value;
  }

  isAllowed(sub: Subscription | undefined, permissionName: string) {
    const value = this.getPermissionValue(sub, permissionName);
    return value && value !== 'false';
  }

  isPaid(sub: Subscription | undefined, permissionName: string) {
    const value = this.getPermissionValue(sub, permissionName);
    return value && value !== 'true' && value !== 'false';
  }

  getPrice(sub: Subscription | undefined, permissionName: string) {
    if (this.isPaid(sub, permissionName)) {
      return this.getPermissionValue(sub, permissionName);
    }
    return undefined;
  }

  getPlanPriceText(plan: PaymentPlan, permission: string, label = '') {
    let result = this.getPlanPermission(plan, permission);
    return result !== 'true' && result !== 'false' ? '$' + result + label : result;
  }

  getPlanPermission(plan: PaymentPlan, permission: string) {
    let result = (plan.permissions || []).find((perm) => perm.name === permission) || {};
    return result.value || 'false';
  }

  minimumMonthsEndDate(subscription: Subscription | undefined) {
    if (!subscription || !subscription.plan) return undefined;
    let minimumMonths = parseInt(
      this.getPlanPermission(subscription.plan, this.getPaymentPlanPermissions().minimum_months),
    );
    let startDate = subscription.created!;
    return moment.unix(startDate).add(minimumMonths, 'months').unix();
  }

  stillInMinimumMonths(subscription: Subscription | undefined) {
    let date = this.minimumMonthsEndDate(subscription);
    if (!date) return false;
    return moment().isBefore(moment.unix(date));
  }
}

const PAYMENT_PLAN_PERMISSSIONS = {
  video_appointment: 'video_appointment',
  clinic_appointment: 'clinic_appointment',
  home_appointment: 'home_appointment',
  lab_appointment: 'lab_appointment',
  minimum_months: 'minimum_months',
} as const;

export default new SubscriptionUtils();
